/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  Alert,
  CircularProgress,
} from '@mui/material';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import { apiPost } from '../../utils/apiClient';
import DownloadExcelTemplateButton from './DownloadExcelTemplateButton';
import { notifyAPIClient } from '../../utils/toast';
import { useTranslation } from '../../i18n';

interface WorkbookUploadResult {
  results: { row: any; status: string; registrationId: number }[];
  errors: { row: any; error: string }[];
}

type WorkbookUploadResponse = {
  status: number;
  data: WorkbookUploadResult;
  message: string;
};

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const RegistrationImportButton: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Open/close dialog
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setAlert({ open: false, message: '', severity: 'success' });
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (
      selectedFile &&
      selectedFile.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFile(selectedFile);
      setAlert({ open: false, message: '', severity: 'success' });
    } else {
      setFile(null);
      setAlert({
        open: true,
        message: t('registration.invalidExcelFile'),
        severity: 'error',
      });
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setAlert({
        open: true,
        message: t('registration.noFileSelected'),
        severity: 'error',
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiPost<WorkbookUploadResponse>(
        '/registration/import-excel',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      const { results, errors } = response.data;
      if (errors && errors.length > 0) {
        setAlert({
          open: true,
          message: t('registration.processedWithErrors', {
            count: errors.length,
          }),
          severity: 'error',
        });
        console.log('Errors:', errors);
      } else {
        setAlert({
          open: true,
          message: t('registration.successfullyProcessed', {
            count: results.length,
          }),
          severity: 'success',
        });
        notifyAPIClient(t('registration.importSuccess'));
      }
      console.log('Results:', results);
      handleClose();
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        startIcon={<FileOpenIcon />}
        className="bg-blue-600 hover:bg-blue-700 !text-white font-semibold py-2 px-4 rounded"
      >
        {t('registration.importData')}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          className="flex items-center justify-between"
          fontWeight="bold"
        >
          {t('registration.uploadExcelFile')}
          <DownloadExcelTemplateButton />
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <Input
              type="file"
              inputProps={{ accept: '.xlsx' }}
              onChange={handleFileChange}
              fullWidth
              className="mb-4"
            />
            {alert.open && (
              <Alert
                severity={alert.severity}
                onClose={() => setAlert({ ...alert, open: false })}
              >
                {alert.message}
              </Alert>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={!file || uploading}
            className="bg-blue-600 hover:bg-blue-700 !text-white"
          >
            {uploading ? (
              <CircularProgress size={24} className="text-white" />
            ) : (
              t('registration.upload')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegistrationImportButton;
